import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRefreshEffect } from "@/context/RefreshContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Archive, ArchiveRestore, Trash2, Inbox, ArrowDownUp } from "lucide-react";
import FormDialog from "@/components/FormDialog";

export default function RecordsSection({
  endpoint,
  columns,
  fields,
  makeDefault,
  extraQuery = {},
  mode = "active", // "active" | "archive"
  createLabel = "Eintrag erstellen",
  emptyLabel = "Keine Einträge vorhanden",
  testidPrefix = "record",
  showArchive = true,
  renderExtraActions,
  dateSortKey = null,
  rowClass = null,
}) {
  const [sortDir, setSortDir] = useState("desc");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type, row}

  const archived = mode === "archive";
  const queryKey = JSON.stringify(extraQuery);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { params: { archived, ...extraQuery } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, archived, queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  useRefreshEffect(load);

  const openCreate = () => {
    setEditing(null);
    setValues(makeDefault());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setValues({ ...row });
    setDialogOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = fields.reduce((acc, f) => ({ ...acc, [f.name]: values[f.name] ?? "" }), {});
      // preserve fixed fields (e.g. kategorie) from defaults
      Object.keys(extraQuery).forEach((k) => (payload[k] = values[k] ?? extraQuery[k]));
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Eintrag aktualisiert");
      } else {
        await api.post(endpoint, payload);
        toast.success("Eintrag erstellt");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const doAction = async () => {
    if (!confirm) return;
    const { type, row } = confirm;
    try {
      if (type === "delete") {
        await api.delete(`${endpoint}/${row.id}`);
        toast.success("Eintrag gelöscht");
      } else {
        await api.patch(`${endpoint}/${row.id}/archive`, null, {
          params: { archived: !archived },
        });
        toast.success(archived ? "Wiederhergestellt" : "Archiviert");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aktion fehlgeschlagen");
    } finally {
      setConfirm(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q));
  });

  const sorted = dateSortKey
    ? [...filtered].sort((a, b) => {
        const av = String(a[dateSortKey] ?? "");
        const bv = String(b[dateSortKey] ?? "");
        return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      })
    : filtered;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid={`${testidPrefix}-search`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
            className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
          />
        </div>
        {dateSortKey && (
          <Button
            data-testid={`${testidPrefix}-sort-date`}
            variant="outline"
            onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
            className="rounded-sm border-zinc-700 text-zinc-300 gap-2"
            title="Sortierung nach Datum umschalten"
          >
            <ArrowDownUp className="h-4 w-4" />
            {sortDir === "desc" ? "Neueste zuerst" : "Älteste zuerst"}
          </Button>
        )}
        {!archived && (
          <div className="ml-auto flex items-center gap-2">
            {renderExtraActions && renderExtraActions(load)}
            <Button
              data-testid={`${testidPrefix}-create-btn`}
              onClick={openCreate}
              className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-2"
            >
              <Plus className="h-4 w-4" />
              {createLabel}
            </Button>
          </div>
        )}
      </div>

      <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 h-11"
                >
                  {c.label}
                </TableHead>
              ))}
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 w-[140px]">
                Aktionen
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={columns.length + 1} className="text-center py-10 text-zinc-500 font-mono text-sm">
                  Wird geladen…
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={columns.length + 1} className="text-center py-14">
                  <div className="flex flex-col items-center gap-2 text-zinc-600">
                    <Inbox className="h-8 w-8" />
                    <span className="font-mono text-sm">{emptyLabel}</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row) => (
                <TableRow
                  key={row.id}
                  data-testid={`${testidPrefix}-row`}
                  className={`border-zinc-800 transition-colors duration-150 ${(rowClass && rowClass(row)) || "hover:bg-zinc-900/50"}`}
                >
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.mono ? "font-mono text-sm text-zinc-300 align-top break-words [overflow-wrap:anywhere]" : "text-sm text-zinc-300 align-top break-words [overflow-wrap:anywhere]"}>
                      {c.render ? c.render(row[c.key], row) : row[c.key] || <span className="text-zinc-600">—</span>}
                    </TableCell>
                  ))}
                  <TableCell className="text-right align-top">
                    <div className="flex items-center justify-end gap-1">
                      {!archived && (
                        <Button
                          data-testid={`${testidPrefix}-edit`}
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(row)}
                          className="h-8 w-8 rounded-sm text-zinc-400 hover:text-primary hover:bg-primary/10"
                          title="Bearbeiten"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        data-testid={`${testidPrefix}-archive`}
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirm({ type: "archive", row })}
                        className={`h-8 w-8 rounded-sm text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10 ${showArchive ? "" : "hidden"}`}
                        title={archived ? "Wiederherstellen" : "Archivieren"}
                      >
                        {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </Button>
                      <Button
                        data-testid={`${testidPrefix}-delete`}
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirm({ type: "delete", row })}
                        className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? "Eintrag bearbeiten" : createLabel}
        fields={fields}
        values={values}
        setValues={setValues}
        onSubmit={submit}
        submitting={submitting}
        testidPrefix={testidPrefix}
      />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">
              {confirm?.type === "delete"
                ? "Eintrag löschen?"
                : archived
                ? "Eintrag wiederherstellen?"
                : "Eintrag archivieren?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirm?.type === "delete"
                ? "Dieser Vorgang kann nicht rückgängig gemacht werden."
                : archived
                ? "Der Eintrag wird zurück in den aktiven Bereich verschoben."
                : "Der Eintrag wird in das Archiv verschoben."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`${testidPrefix}-confirm`}
              onClick={doAction}
              className={`rounded-sm ${
                confirm?.type === "delete"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-primary hover:bg-blue-600"
              } text-white`}
            >
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
